"use client";

import {
  type InfiniteData,
  keepPreviousData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type CommentLikeListResponse,
  type CommentListItem,
  type CommentListResponse,
  createComment,
  createPollPost,
  createPost,
  deleteComment,
  deletePost,
  type FeedPost,
  type FeedRequest,
  type FeedResponse,
  getFeed,
  type LikeListResponse,
  listCommentLikes,
  listComments,
  listLikes,
  quoteFeedEntity,
  repostPost,
  setCommentPinned,
  toggleCommentLike,
  togglePostBookmark,
  togglePostLike,
  votePostPoll,
} from "@/actions/post";
import type {
  MediaData,
  PostLocationTag,
  PostTagContextType,
  PostVisibility,
} from "@/db/schema/post";
import { useBotReplyCacheSync } from "@/hooks/use-bot-reply-cache-sync";
import {
  type CommentThreadPosition,
  createOptimisticCommentId,
  deriveOptimisticCommentThreadPosition,
  getCommentSubtreeIds,
} from "@/lib/comments/threading";
import { botReplyRefetchInterval } from "@/lib/hiive-bot/polling";
import { mediaUrlsFromArray } from "@/lib/media";
import {
  extractMentionMatches,
  matchesToUnresolvedMentions,
} from "@/lib/mentions";
import {
  type MusicAttachment,
  type MusicAttachmentInput,
  musicAttachmentInput,
} from "@/lib/music/attachment";
import {
  PROFILE_REPLIES_ROOT_KEY,
  profileTabKeys,
} from "@/lib/query-keys/profile-tabs";
import type {
  CreateCommentInput,
  CreatePollPostInput,
  CreatePostInput,
  QuoteFeedEntityInput,
} from "@/validations/post";

/**
 * `music` is optional here (the schema defaults it); `musicPreview` is the
 * picked song's full snapshot, client-only, so the optimistic card shows the
 * pill at once — the server never sees it and re-reads the song itself.
 */
type PostMusicFields = {
  music?: MusicAttachmentInput | null;
  musicPreview?: MusicAttachment | null;
};

type CreatePostPayload = Omit<CreatePostInput, "music"> &
  PostMusicFields & {
    viewer?: PostViewerInfo;
  };

type CreatePollPostPayload = Omit<CreatePollPostInput, "music"> &
  PostMusicFields & {
    viewer?: PostViewerInfo;
  };

type CreateCommentPayload = Omit<CreateCommentInput, "postId" | "music"> & {
  postId: number;
  viewer?: CommentViewerInfo;
  /** A song reply: the full snapshot paints the optimistic card; only its id goes up. */
  song?: MusicAttachment | null;
};

type CommentViewerInfo = {
  id?: string;
  displayName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
};

export type PostViewerInfo = CommentViewerInfo;

type FeedInfiniteData = InfiniteData<FeedResponse, number | null>;
type CommentsPage = Omit<CommentListResponse, "comments"> & {
  comments: Array<CommentListItem & CommentMeta>;
};
type CommentsInfiniteData = InfiniteData<CommentsPage, number | null>;
type LikesPage = LikeListResponse;

type CommentMeta = {
  tempId?: string;
  isPending?: boolean;
};

type FeedMeta = {
  tempId?: number;
  isOptimistic?: boolean;
};

type TogglePostLikeVariables = {
  postId: number;
  currentlyLiked: boolean;
};

type TogglePostBookmarkVariables = {
  postId: number;
  currentlyBookmarked: boolean;
};

type RepostPostVariables = {
  postId: number;
  currentlyReposted: boolean;
  isQuote?: boolean;
  quoteContent?: string | null;
  mediaUrls?: MediaData[];
  locationTag?: PostLocationTag | null;
  contextTag?: { type: PostTagContextType; id: number } | null;
  poll?: {
    question: string;
    options: string[];
    closesAt?: Date | null;
  } | null;
  visibility?: PostVisibility;
};

type VotePostPollVariables = {
  postId: number;
  optionId: number;
};

type ToggleCommentLikeVariables = {
  commentId: number;
  postId: number;
  currentlyLiked: boolean;
};

type FeedSnapshot = {
  queryKey: readonly unknown[];
  data: FeedInfiniteData | undefined;
};

export const postsKeys = {
  all: ["posts"] as const,
  feedRoot: ["posts", "feed"] as const,
  feed: (context: FeedRequest, limit: number) => {
    if (context.type === "user") {
      return profileTabKeys.userPosts(
        context.userId,
        context.includePrivatePreview ?? false,
        limit,
        context.mediaOnly ?? false,
      );
    }

    return [
      "posts",
      "feed",
      context.type,
      context.type === "hiive" ? context.hiiveId : context.viewerId,
      limit,
      "default",
    ] as const;
  },
  comments: (postId: number) => ["posts", "comments", postId] as const,
  likes: (postId: number) => ["posts", "likes", postId] as const,
  commentLikes: (commentId: number) =>
    ["posts", "comment-likes", commentId] as const,
};

export function usePostFeed(
  context: FeedRequest,
  limit: number,
  initialPosts?: FeedPost[],
  options?: {
    enabled?: boolean;
    initialFeed?: FeedResponse;
  },
) {
  const enabled = options?.enabled ?? true;
  const queryKey = postsKeys.feed(context, limit);

  // Format initial data for the infinite query. Prefer a full first-page
  // response (`initialFeed`) when available so pagination (hasMore/nextCursor)
  // stays correct; fall back to a bare posts array otherwise.
  const initialData: FeedInfiniteData | undefined = options?.initialFeed
    ? {
        pages: [options.initialFeed],
        pageParams: [null],
      }
    : initialPosts
      ? {
          pages: [
            {
              posts: initialPosts,
              hasMore: false, // Assume no more pages initially
              nextCursor: null,
            },
          ],
          pageParams: [null],
        }
      : undefined;

  const query = useInfiniteQuery({
    queryKey,
    enabled,
    queryFn: async ({ pageParam }) => {
      const result = await getFeed({
        ...context,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch posts");
      }

      return result.data;
    },
    initialPageParam: null as number | null,
    initialData,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 30_000,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  });

  return query;
}

export function useCreatePostMutation(limit: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["posts", "create"],
    retry: false,
    mutationFn: async (payload: CreatePostPayload) => {
      const { musicPreview: _musicPreview, ...input } = payload;
      const result = await createPost({ ...input, music: input.music ?? null });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to create post");
      }

      return { id: result.data.id, payload };
    },
    onMutate: async (payload) => {
      const contexts = getFeedContextsFromPostPayload(payload, limit);

      const optimisticPost: FeedPost & FeedMeta = buildOptimisticPost(payload);

      const snapshots = updateSpecificFeeds(queryClient, contexts, (old) =>
        prependPost(old, optimisticPost),
      );

      return {
        snapshots,
        optimisticId: optimisticPost.id,
        contexts,
      };
    },
    onError: (_err, _payload, ctx) => {
      if (!ctx?.snapshots) return;
      restoreFeedSnapshots(queryClient, ctx.snapshots);
    },
    onSuccess: (result, _payload, ctx) => {
      if (!ctx) return;
      updateSpecificFeeds(queryClient, ctx.contexts, (old) =>
        replacePostId(old, ctx.optimisticId, result.id),
      );
    },
    onSettled: (_data, _error, payload, ctx) => {
      if (!ctx) return;
      ctx.contexts.forEach((context) => {
        queryClient.invalidateQueries({
          queryKey: postsKeys.feed(context.request, context.limit),
        });
      });
      if (payload.viewer?.id) {
        queryClient.invalidateQueries({
          queryKey: ["posts", "feed", "user", payload.viewer.id],
        });
      }
    },
  });
}

export function useCreatePollPostMutation(limit: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["posts", "polls", "create"],
    retry: false,
    mutationFn: async (payload: CreatePollPostPayload) => {
      const { musicPreview: _musicPreview, ...input } = payload;
      const result = await createPollPost({
        ...input,
        music: input.music ?? null,
      });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to create poll");
      }

      return { id: result.data.id, payload };
    },
    onMutate: async (payload) => {
      const contexts = payload.viewer?.id
        ? [
            {
              request: { type: "feed" as const, viewerId: payload.viewer.id },
              limit,
            },
            {
              request: { type: "user" as const, userId: payload.viewer.id },
              limit,
            },
            {
              request: {
                type: "user" as const,
                userId: payload.viewer.id,
                includePrivatePreview: true,
              },
              limit,
            },
          ]
        : [];

      const optimisticPost = buildOptimisticPollPost(payload);
      const snapshots = updateSpecificFeeds(queryClient, contexts, (old) =>
        prependPost(old, optimisticPost),
      );

      return {
        snapshots,
        optimisticId: optimisticPost.id,
        contexts,
      };
    },
    onError: (_err, _payload, ctx) => {
      if (!ctx?.snapshots) return;
      restoreFeedSnapshots(queryClient, ctx.snapshots);
    },
    onSuccess: (result, _payload, ctx) => {
      if (!ctx) return;
      updateSpecificFeeds(queryClient, ctx.contexts, (old) =>
        replacePostId(old, ctx.optimisticId, result.id),
      );
    },
    onSettled: (_data, _error, payload, ctx) => {
      if (!ctx) return;
      ctx.contexts.forEach((context) => {
        queryClient.invalidateQueries({
          queryKey: postsKeys.feed(context.request, context.limit),
        });
      });
      if (payload.viewer?.id) {
        queryClient.invalidateQueries({
          queryKey: ["posts", "feed", "user", payload.viewer.id],
        });
      }
    },
  });
}

export function useTogglePostLikeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["posts", "toggle-like"],
    mutationFn: async ({ postId }: TogglePostLikeVariables) => {
      const result = await togglePostLike({ postId });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to toggle like");
      }
      return { liked: result.data.liked };
    },
    onMutate: async ({ postId, currentlyLiked }: TogglePostLikeVariables) => {
      const delta = currentlyLiked ? -1 : 1;
      const snapshots = updateAllFeeds(queryClient, (old) =>
        updatePost(old, postId, (post) => ({
          ...post,
          likeCount: Math.max(0, post.likeCount + delta),
          isLikedByViewer: !currentlyLiked,
        })),
      );

      // Force a re-render by invalidating queries
      queryClient.invalidateQueries({
        queryKey: postsKeys.feedRoot,
        refetchType: "none",
      });

      return {
        snapshots,
        postId,
      };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx?.snapshots) return;
      restoreFeedSnapshots(queryClient, ctx.snapshots);
      // Force re-render after rollback
      queryClient.invalidateQueries({
        queryKey: postsKeys.feedRoot,
        refetchType: "none",
      });
    },
    onSuccess: (result, vars) => {
      updateAllFeeds(queryClient, (old) =>
        updatePost(old, vars.postId, (post) => {
          const needsCorrection = post.isLikedByViewer !== result.liked;
          if (!needsCorrection) {
            return post;
          }

          return {
            ...post,
            isLikedByViewer: result.liked,
            likeCount: Math.max(0, post.likeCount + (result.liked ? 1 : -1)),
          };
        }),
      );

      // Force re-render and invalidate likes cache
      queryClient.invalidateQueries({ queryKey: postsKeys.likes(vars.postId) });
      queryClient.invalidateQueries({
        queryKey: postsKeys.feedRoot,
        refetchType: "none",
      });
    },
  });
}

export function useTogglePostBookmarkMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["posts", "toggle-bookmark"],
    mutationFn: async ({ postId }: TogglePostBookmarkVariables) => {
      const result = await togglePostBookmark({ postId });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to save post");
      }
      return { bookmarked: result.data.bookmarked };
    },
    onMutate: async ({
      postId,
      currentlyBookmarked,
    }: TogglePostBookmarkVariables) => {
      const delta = currentlyBookmarked ? -1 : 1;
      const snapshots = updateAllFeeds(queryClient, (old) =>
        updatePost(old, postId, (post) => ({
          ...post,
          bookmarkCount: Math.max(0, post.bookmarkCount + delta),
          isBookmarkedByViewer: !currentlyBookmarked,
        })),
      );

      queryClient.invalidateQueries({
        queryKey: postsKeys.feedRoot,
        refetchType: "none",
      });

      return { snapshots, postId };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx?.snapshots) return;
      restoreFeedSnapshots(queryClient, ctx.snapshots);
      queryClient.invalidateQueries({
        queryKey: postsKeys.feedRoot,
        refetchType: "none",
      });
    },
    onSuccess: (result, vars) => {
      updateAllFeeds(queryClient, (old) =>
        updatePost(old, vars.postId, (post) => {
          if (post.isBookmarkedByViewer === result.bookmarked) {
            return post;
          }

          return {
            ...post,
            isBookmarkedByViewer: result.bookmarked,
            bookmarkCount: Math.max(
              0,
              post.bookmarkCount + (result.bookmarked ? 1 : -1),
            ),
          };
        }),
      );
    },
  });
}

export function useRepostPostMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["posts", "repost"],
    mutationFn: async ({
      currentlyReposted: _currentlyReposted,
      ...input
    }: RepostPostVariables) => {
      const result = await repostPost(input);
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to repost");
      }
      return result.data;
    },
    onMutate: async ({
      postId,
      currentlyReposted,
      isQuote,
      quoteContent,
    }: RepostPostVariables) => {
      const hasQuote = Boolean(isQuote || quoteContent?.trim());
      const delta = hasQuote
        ? currentlyReposted
          ? 0
          : 1
        : currentlyReposted
          ? -1
          : 1;
      const nextReposted = hasQuote ? true : !currentlyReposted;
      const snapshots = updateAllFeeds(queryClient, (old) =>
        updatePostsByRepostTarget(old, postId, (post) => ({
          ...post,
          repostCount: Math.max(0, post.repostCount + delta),
          isRepostedByViewer: nextReposted,
        })),
      );

      queryClient.invalidateQueries({
        queryKey: postsKeys.feedRoot,
        refetchType: "none",
      });

      return { snapshots, postId };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx?.snapshots) return;
      restoreFeedSnapshots(queryClient, ctx.snapshots);
      queryClient.invalidateQueries({
        queryKey: postsKeys.feedRoot,
        refetchType: "none",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: postsKeys.feedRoot });
    },
  });
}

export function useQuoteFeedEntityMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["posts", "quote-feed-entity"],
    mutationFn: async (input: QuoteFeedEntityInput) => {
      const result = await quoteFeedEntity(input);
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to quote repost");
      }
      return result.data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: postsKeys.feedRoot });
      void queryClient.invalidateQueries({ queryKey: ["activityPosts"] });
      void queryClient.invalidateQueries({ queryKey: ["activitySessions"] });
      void queryClient.invalidateQueries({ queryKey: ["socialFeed"] });
    },
  });
}

export function useVotePostPollMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["posts", "polls", "vote"],
    mutationFn: async ({ postId, optionId }: VotePostPollVariables) => {
      const result = await votePostPoll({ postId, optionId });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to vote");
      }
      return { optionId: result.data.optionId };
    },
    onMutate: async ({ postId, optionId }: VotePostPollVariables) => {
      const snapshots = updateAllFeeds(queryClient, (old) =>
        updatePost(old, postId, (post) => updatePollVote(post, optionId)),
      );

      queryClient.invalidateQueries({
        queryKey: postsKeys.feedRoot,
        refetchType: "none",
      });

      return { snapshots, postId };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx?.snapshots) return;
      restoreFeedSnapshots(queryClient, ctx.snapshots);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: postsKeys.feedRoot,
        refetchType: "none",
      });
    },
  });
}

export function useDeletePostMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["posts", "delete"],
    mutationFn: async ({ id }: { id: number }) => {
      const result = await deletePost({ id });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to delete post");
      }
      return { id };
    },
    onMutate: async ({ id }: { id: number }) => {
      const snapshots = updateAllFeeds(queryClient, (old) =>
        removePost(old, id),
      );

      return { snapshots, id };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx?.snapshots) return;
      restoreFeedSnapshots(queryClient, ctx.snapshots);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: postsKeys.feedRoot });
    },
  });
}

export function useComments(
  postId: number | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { enabled = true, limit = 100 } = options ?? {};

  const query = useInfiniteQuery({
    queryKey: postsKeys.comments(postId ?? 0),
    enabled: Boolean(postId && enabled),
    queryFn: async ({ pageParam }) => {
      if (!postId) {
        return {
          comments: [],
          hasMore: false,
          nextCursor: null,
        } satisfies CommentsPage;
      }

      const result = await listComments({
        postId,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch comments");
      }

      return {
        ...result.data,
        comments: result.data.comments.map((comment) => ({
          ...comment,
          isPending: false,
        })),
      } satisfies CommentsPage;
    },
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    refetchInterval: (query) =>
      botReplyRefetchInterval(
        query.state.data?.pages.flatMap((page) => page.comments) ?? [],
      ),
    staleTime: 15_000,
    // Other people change this list, and notifications link straight into
    // it; with `false` the 24h cache served it stale until an app restart.
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    refetchIntervalInBackground: false,
  });
  useBotReplyCacheSync(
    `post:${postId}`,
    query.data?.pages.flatMap((page) => page.comments) ?? [],
    () => {
      if (postId && enabled) void query.refetch();
    },
  );
  return query;
}

export function useCreateCommentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["posts", "comments", "create"],
    mutationFn: async (payload: CreateCommentPayload) => {
      const { song, ...input } = payload;
      const result = await createComment({
        ...input,
        music: song ? musicAttachmentInput(song) : null,
      });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to create comment");
      }
      return { id: result.data.id, payload };
    },
    onMutate: async (payload) => {
      const key = postsKeys.comments(payload.postId);
      await queryClient.cancelQueries({ queryKey: key });

      const prev = queryClient.getQueryData<CommentsInfiniteData>(key);
      const threadPosition = deriveOptimisticCommentThreadPosition(
        payload.parentCommentId,
        prev?.pages,
      );
      const optimistic = threadPosition
        ? buildOptimisticComment(payload, threadPosition)
        : null;

      if (optimistic) {
        queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
          prependComment(old, optimistic),
        );
      }

      const feedSnapshots = updateAllFeeds(queryClient, (old) =>
        updatePost(old, payload.postId, (post) => ({
          ...post,
          commentCount: post.commentCount + 1,
        })),
      );

      return {
        prev,
        optimisticId: optimistic?.tempId,
        postId: payload.postId,
        feedSnapshots,
      };
    },
    onError: (_err, payload, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(postsKeys.comments(payload.postId), ctx.prev);
      }
      if (ctx?.feedSnapshots) {
        restoreFeedSnapshots(queryClient, ctx.feedSnapshots);
      }
    },
    onSuccess: (result, payload, ctx) => {
      const key = postsKeys.comments(payload.postId);
      queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
        replaceCommentId(old, ctx?.optimisticId, result.id),
      );
    },
    onSettled: (_data, _error, payload) => {
      queryClient.invalidateQueries({
        queryKey: postsKeys.comments(payload.postId),
      });
      // Your replies tab lists this comment too.
      queryClient.invalidateQueries({ queryKey: PROFILE_REPLIES_ROOT_KEY });
    },
  });
}

export function useDeleteCommentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["posts", "comments", "delete"],
    mutationFn: async ({ id }: { id: number; postId: number }) => {
      const result = await deleteComment({ id });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to delete comment");
      }
      return { id };
    },
    onMutate: async ({ id, postId }: { id: number; postId: number }) => {
      const key = postsKeys.comments(postId);
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<CommentsInfiniteData>(key);
      const deletedCount = prev
        ? getCommentSubtreeIds(
            prev.pages.flatMap((page) => page.comments),
            id,
          ).size
        : 1;

      queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
        removeComment(old, id),
      );

      const feedSnapshots = updateAllFeeds(queryClient, (old) =>
        updatePost(old, postId, (post) => ({
          ...post,
          commentCount: Math.max(0, post.commentCount - deletedCount),
        })),
      );

      return { prev, postId, feedSnapshots };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(postsKeys.comments(vars.postId), ctx.prev);
      }
      if (ctx?.feedSnapshots) {
        restoreFeedSnapshots(queryClient, ctx.feedSnapshots);
      }
    },
    onSettled: (_data, _error, vars) => {
      queryClient.invalidateQueries({
        queryKey: postsKeys.comments(vars.postId),
      });
    },
  });
}

export function useToggleCommentLikeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["posts", "comments", "toggle-like"],
    mutationFn: async ({ commentId }: ToggleCommentLikeVariables) => {
      const result = await toggleCommentLike({ commentId });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to toggle like");
      }
      return { liked: result.data.liked };
    },
    onMutate: async ({
      commentId,
      postId,
      currentlyLiked,
    }: ToggleCommentLikeVariables) => {
      const key = postsKeys.comments(postId);
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<CommentsInfiniteData>(key);

      queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
        updateComment(old, commentId, (comment) => ({
          ...comment,
          isLikedByViewer: !currentlyLiked,
          likeCount: Math.max(0, comment.likeCount + (currentlyLiked ? -1 : 1)),
        })),
      );

      return { prev, postId };
    },
    onError: (_err, vars, ctx) => {
      if (!ctx?.prev) return;
      queryClient.setQueryData(postsKeys.comments(vars.postId), ctx.prev);
    },
    onSettled: (_data, _error, vars) => {
      queryClient.invalidateQueries({
        queryKey: postsKeys.comments(vars.postId),
      });
    },
  });
}

export function useSetCommentPinnedMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["posts", "comments", "pin"],
    mutationFn: async (input: {
      postId: number;
      commentId: number;
      isPinned: boolean;
    }) => {
      const result = await setCommentPinned(input);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    },
    onSettled: (_data, _error, input) => {
      queryClient.invalidateQueries({
        queryKey: postsKeys.comments(input.postId),
      });
    },
  });
}

export function usePostLikes(
  postId: number | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { enabled = true, limit = 30 } = options ?? {};

  return useInfiniteQuery({
    queryKey: postsKeys.likes(postId ?? 0),
    enabled: Boolean(postId && enabled),
    queryFn: async ({ pageParam }) => {
      if (!postId) {
        return {
          likes: [],
          hasMore: false,
          nextCursor: null,
        } satisfies LikesPage;
      }

      const result = await listLikes({
        postId,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch likes");
      }

      return result.data;
    },
    initialPageParam: null as LikesPage["nextCursor"],
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 15_000,
    // Other people change this list, and notifications link straight into
    // it; with `false` the 24h cache served it stale until an app restart.
    refetchOnMount: true,
    placeholderData: keepPreviousData,
  });
}

type CommentLikesPage = CommentLikeListResponse;

export function useCommentLikes(
  commentId: number | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { enabled = true, limit = 30 } = options ?? {};

  return useInfiniteQuery({
    queryKey: postsKeys.commentLikes(commentId ?? 0),
    enabled: Boolean(commentId && enabled),
    queryFn: async ({ pageParam }) => {
      if (!commentId) {
        return {
          likes: [],
          hasMore: false,
          nextCursor: null,
        } satisfies CommentLikesPage;
      }

      const result = await listCommentLikes({
        commentId,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch comment likes");
      }

      return result.data;
    },
    initialPageParam: null as CommentLikesPage["nextCursor"],
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 15_000,
    // Other people change this list, and notifications link straight into
    // it; with `false` the 24h cache served it stale until an app restart.
    refetchOnMount: true,
    placeholderData: keepPreviousData,
  });
}

function buildOptimisticPost(payload: CreatePostPayload): FeedPost & FeedMeta {
  const tempId = -Date.now();
  const authorName =
    payload.viewer?.displayName || payload.viewer?.username || "You";

  const media = (payload.mediaUrls ?? []).map((item, index) => ({
    ...item,
    order: item.order ?? index,
  }));

  return {
    id: tempId,
    tempId,
    isOptimistic: true,
    type: payload.type,
    entityId: payload.type === "hiive" ? (payload.entityId ?? null) : null,
    kind: "original",
    originalPostId: null,
    originalPost: null,
    quoteTarget: null,
    poll: null,
    author: {
      id: payload.viewer?.id ?? "temp",
      displayName: authorName,
      username: payload.viewer?.username ?? null,
      avatarUrl: payload.viewer?.avatarUrl ?? null,
    },
    content: payload.content ?? null,
    mentions: matchesToUnresolvedMentions(
      extractMentionMatches(payload.content ?? ""),
    ),
    media,
    mediaUrls: mediaUrlsFromArray(media),
    locationTag: payload.locationTag ?? null,
    music: payload.musicPreview ?? null,
    // The payload only carries (type, id) — names/slugs are resolved
    // server-side, so the context chip appears after the server refetch.
    contextTag: null,
    sticker: payload.sticker ?? null,
    authorId: payload.viewer?.id ?? "temp",
    authorName,
    authorImage: payload.viewer?.avatarUrl ?? null,
    visibility: payload.visibility,
    status: payload.status,
    isPinned: payload.isPinned ?? false,
    createdAt: new Date(),
    updatedAt: new Date(),
    likeCount: 0,
    commentCount: 0,
    repostCount: 0,
    bookmarkCount: 0,
    shareCount: 0,
    isLikedByViewer: false,
    isRepostedByViewer: false,
    isBookmarkedByViewer: false,
  } satisfies FeedPost & FeedMeta;
}

function buildOptimisticPollPost(
  payload: CreatePollPostPayload,
): FeedPost & FeedMeta {
  const tempId = -Date.now();
  const authorName =
    payload.viewer?.displayName || payload.viewer?.username || "You";
  const media = (payload.mediaUrls ?? []).map((item, index) => ({
    ...item,
    order: item.order ?? index,
  }));

  return {
    id: tempId,
    tempId,
    isOptimistic: true,
    type: "user",
    entityId: null,
    kind: "poll",
    originalPostId: null,
    originalPost: null,
    quoteTarget: null,
    poll: {
      postId: tempId,
      question: payload.question,
      questionMentions: matchesToUnresolvedMentions(
        extractMentionMatches(payload.question),
      ),
      allowMultiple: false,
      closesAt: payload.closesAt ?? null,
      totalVotes: 0,
      viewerOptionId: null,
      options: payload.options.map((label, index) => ({
        id: tempId - index - 1,
        label,
        voteCount: 0,
        isVotedByViewer: false,
      })),
    },
    author: {
      id: payload.viewer?.id ?? "temp",
      displayName: authorName,
      username: payload.viewer?.username ?? null,
      avatarUrl: payload.viewer?.avatarUrl ?? null,
    },
    content: payload.content ?? null,
    mentions: matchesToUnresolvedMentions(
      extractMentionMatches(payload.content ?? ""),
    ),
    media,
    mediaUrls: mediaUrlsFromArray(media),
    locationTag: payload.locationTag ?? null,
    music: payload.musicPreview ?? null,
    sticker: payload.sticker ?? null,
    authorId: payload.viewer?.id ?? "temp",
    authorName,
    authorImage: payload.viewer?.avatarUrl ?? null,
    visibility: payload.visibility ?? "public",
    status: "published",
    isPinned: false,
    createdAt: new Date(),
    updatedAt: new Date(),
    likeCount: 0,
    commentCount: 0,
    repostCount: 0,
    bookmarkCount: 0,
    shareCount: 0,
    isLikedByViewer: false,
    isRepostedByViewer: false,
    isBookmarkedByViewer: false,
  } satisfies FeedPost & FeedMeta;
}

function updatePollVote(post: FeedPost, optionId: number): FeedPost {
  if (!post.poll) {
    return post;
  }

  const previousOptionId = post.poll.viewerOptionId;
  if (previousOptionId === optionId) {
    return post;
  }

  const options = post.poll.options.map((option) => {
    if (option.id === previousOptionId) {
      return {
        ...option,
        voteCount: Math.max(0, option.voteCount - 1),
        isVotedByViewer: false,
      };
    }

    if (option.id === optionId) {
      return {
        ...option,
        voteCount: option.voteCount + 1,
        isVotedByViewer: true,
      };
    }

    return {
      ...option,
      isVotedByViewer: false,
    };
  });

  return {
    ...post,
    poll: {
      ...post.poll,
      viewerOptionId: optionId,
      totalVotes: post.poll.totalVotes + (previousOptionId == null ? 1 : 0),
      options,
    },
  };
}

function removePost(
  data: FeedInfiniteData | undefined,
  postId: number,
): FeedInfiniteData | undefined {
  if (!data) return data;

  const pages = data.pages.map((page) => ({
    ...page,
    posts: page.posts.filter((post) => post.id !== postId),
  }));

  return {
    ...data,
    pages,
  };
}

function prependPost(
  data: FeedInfiniteData | undefined,
  optimistic: FeedPost,
): FeedInfiniteData | undefined {
  if (!data) return data;
  if (data.pages.length === 0) return data;

  const [first, ...rest] = data.pages;
  const deduped = first.posts.filter((post) => post.id !== optimistic.id);
  const nextFirst = {
    ...first,
    posts: [optimistic, ...deduped],
  };

  return {
    ...data,
    pages: [nextFirst, ...rest],
  };
}

function replacePostId(
  data: FeedInfiniteData | undefined,
  tempId: number | undefined,
  newId: number,
): FeedInfiniteData | undefined {
  if (!data || tempId == null) return data;
  let anyChanged = false;

  const pages = data.pages.map((page) => {
    let pageChanged = false;
    const posts = page.posts.map((post) => {
      if (post.id !== tempId) {
        return post;
      }
      pageChanged = true;
      anyChanged = true;
      return {
        ...post,
        id: newId,
        isOptimistic: false,
      };
    });

    return pageChanged ? { ...page, posts } : page;
  });

  return anyChanged ? { ...data, pages } : data;
}

function updatePost(
  data: FeedInfiniteData | undefined,
  postId: number,
  updater: (post: FeedPost) => FeedPost,
): FeedInfiniteData | undefined {
  if (!data) return data;
  let anyChanged = false;

  const pages = data.pages.map((page) => {
    let pageChanged = false;
    const posts = page.posts.map((post) => {
      if (post.id !== postId) {
        return post;
      }
      pageChanged = true;
      anyChanged = true;
      return updater(post);
    });

    return pageChanged ? { ...page, posts } : page;
  });

  return anyChanged ? { ...data, pages } : data;
}

function updatePostsByRepostTarget(
  data: FeedInfiniteData | undefined,
  targetPostId: number,
  updater: (post: FeedPost) => FeedPost,
): FeedInfiniteData | undefined {
  if (!data) return data;
  let anyChanged = false;

  const pages = data.pages.map((page) => {
    let pageChanged = false;
    const posts = page.posts.map((post) => {
      const repostTargetId = post.originalPostId ?? post.id;
      if (repostTargetId !== targetPostId) {
        return post;
      }
      pageChanged = true;
      anyChanged = true;
      return updater(post);
    });

    return pageChanged ? { ...page, posts } : page;
  });

  return anyChanged ? { ...data, pages } : data;
}

function updateAllFeeds(
  queryClient: QueryClient,
  updater: (data: FeedInfiniteData | undefined) => FeedInfiniteData | undefined,
): FeedSnapshot[] {
  // Find all queries that start with the feed root
  const allQueries = queryClient.getQueryCache().getAll();
  const feedQueries = allQueries.filter((query) => {
    const key = query.queryKey;
    return (
      Array.isArray(key) &&
      key.length >= 2 &&
      key[0] === "posts" &&
      key[1] === "feed"
    );
  });

  const snapshots: FeedSnapshot[] = [];

  for (const query of feedQueries) {
    const key = query.queryKey;
    const prev = query.state.data as FeedInfiniteData | undefined;
    snapshots.push({ queryKey: key, data: prev });
    const next = updater(prev);
    if (next !== prev) {
      queryClient.setQueryData(key, next);
    }
  }

  return snapshots;
}

function restoreFeedSnapshots(
  queryClient: QueryClient,
  snapshots: FeedSnapshot[],
) {
  for (const snapshot of snapshots) {
    queryClient.setQueryData(snapshot.queryKey, snapshot.data);
  }
}

function buildOptimisticComment(
  payload: CreateCommentPayload,
  threadPosition: CommentThreadPosition,
): CommentListItem & CommentMeta {
  const id = createOptimisticCommentId();
  const tempId = `temp-comment-${Math.abs(id)}`;
  const authorName =
    payload.viewer?.displayName || payload.viewer?.username || "You";

  return {
    id,
    tempId,
    isPending: true,
    postId: payload.postId,
    parentCommentId: threadPosition.parentCommentId,
    rootCommentId: threadPosition.rootCommentId,
    depth: threadPosition.depth,
    content: payload.content,
    voice: payload.voice ?? null,
    media: payload.media ?? null,
    music: payload.song ?? null,
    isPinned: false,
    pinnedAt: null,
    mentions: matchesToUnresolvedMentions(
      extractMentionMatches(payload.content),
    ),
    createdAt: new Date(),
    author: {
      id: payload.viewer?.id ?? "temp",
      displayName: authorName,
      username: payload.viewer?.username ?? null,
      avatarUrl: payload.viewer?.avatarUrl ?? null,
    },
    likeCount: 0,
    isLikedByViewer: false,
  } satisfies CommentListItem & CommentMeta;
}

function prependComment(
  data: CommentsInfiniteData | undefined,
  comment: CommentListItem & CommentMeta,
): CommentsInfiniteData | undefined {
  if (!data) {
    return data;
  }
  const [first, ...rest] = data.pages;
  if (!first) {
    return data;
  }

  const deduped = first.comments.filter(
    (existing) =>
      (existing.tempId ?? existing.id) !== (comment.tempId ?? comment.id),
  );
  const nextFirst: CommentsPage = {
    ...first,
    comments: [comment, ...deduped],
  };

  return {
    ...data,
    pages: [nextFirst, ...rest],
  };
}

function replaceCommentId(
  data: CommentsInfiniteData | undefined,
  tempId: string | undefined,
  newId: number,
): CommentsInfiniteData | undefined {
  if (!data || !tempId) return data;

  const pages = data.pages.map((page) => {
    const comments = page.comments.map((comment) => {
      if (comment.tempId !== tempId) {
        return comment;
      }
      return {
        ...comment,
        id: newId,
        tempId: undefined,
        isPending: false,
      };
    });

    return {
      ...page,
      comments,
    };
  });

  return {
    ...data,
    pages,
  };
}

function removeComment(
  data: CommentsInfiniteData | undefined,
  id: number,
): CommentsInfiniteData | undefined {
  if (!data) return data;

  const removedIds = getCommentSubtreeIds(
    data.pages.flatMap((page) => page.comments),
    id,
  );

  const pages = data.pages.map((page) => ({
    ...page,
    comments: page.comments.filter((comment) => !removedIds.has(comment.id)),
  }));

  return {
    ...data,
    pages,
  };
}

function updateComment(
  data: CommentsInfiniteData | undefined,
  commentId: number,
  updater: (
    comment: CommentListItem & CommentMeta,
  ) => CommentListItem & CommentMeta,
): CommentsInfiniteData | undefined {
  if (!data) return data;

  const pages = data.pages.map((page) => ({
    ...page,
    comments: page.comments.map((comment) =>
      comment.id === commentId ? updater(comment) : comment,
    ),
  }));

  return {
    ...data,
    pages,
  };
}

function updateSpecificFeeds(
  queryClient: QueryClient,
  contexts: Array<{ request: FeedRequest; limit: number }>,
  updater: (data: FeedInfiniteData | undefined) => FeedInfiniteData | undefined,
): FeedSnapshot[] {
  const snapshots: FeedSnapshot[] = [];

  for (const context of contexts) {
    const key = postsKeys.feed(context.request, context.limit);
    const prev = queryClient.getQueryData<FeedInfiniteData>(key);
    snapshots.push({ queryKey: key, data: prev });
    if (prev && !isFeedInfiniteData(prev)) {
      continue;
    }
    const next = updater(prev);
    if (next !== prev) {
      queryClient.setQueryData(key, next);
    }
  }

  return snapshots;
}

function isFeedInfiniteData(value: unknown): value is FeedInfiniteData {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<FeedInfiniteData>;
  return Array.isArray(candidate.pages) && Array.isArray(candidate.pageParams);
}

function getFeedContextsFromPostPayload(
  payload: CreatePostPayload,
  limit: number,
) {
  const contexts: Array<{ request: FeedRequest; limit: number }> = [];

  if (payload.type === "hiive" && payload.entityId != null) {
    contexts.push({
      request: { type: "hiive", hiiveId: payload.entityId },
      limit,
    });
  }

  if (payload.viewer?.id) {
    contexts.push({
      request: { type: "feed", viewerId: payload.viewer.id },
      limit,
    });
    contexts.push({
      request: { type: "user", userId: payload.viewer.id },
      limit,
    });
    contexts.push({
      request: {
        type: "user",
        userId: payload.viewer.id,
        includePrivatePreview: true,
      },
      limit,
    });
  }

  return contexts;
}
